import { defineComponent, reactive, ref, computed, toRef } from 'vue-demi';
import { a as useIdAttribute, u as useControlAttributes } from './useIdAttribute-3a339b79.js';
import './TimeFormat-a7f5565b.js';
import { i as include, e as exclude } from './include-2a81e823.js';
import './dom-53c9635b.js';
import { i as isArray } from './isArray-8eeb52b9.js';
import { i as isUndefined } from './isUndefined-3eca6e32.js';
import '@vueuse/core';
import { u as useProxyModelValue } from './useProxyModelValue-e18f251e.js';
import '@onereach/styles/screens.json';
import '@onereach/styles/tailwind.config.json';
import { s as script$1, L as LabelVariant } from './OrLabel-4abbce6e.js';
import { s as script$2 } from './OrLoader-5e2f359b.js';
import { resolveComponent, openBlock, createElementBlock, normalizeClass, mergeProps, createVNode, createBlock, withCtx, renderSlot, createTextVNode, toDisplayString, createCommentVNode } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

const Switch = [
// Layout
'layout-row',
// Spacing
'gap-sm+ md:gap-sm'];
const SwitchControl = [
// Layout
'layout-inline-row justify-start checked:justify-end',
// Interactivity
'interactivity-click',
// Shape
'rounded-full',
// Box
'w-[43px] md:w-[37px]', 'h-[24px] md:h-[21px]',
// Spacing
'px-[2px]', 'active:px-[1px]',
// Spacing (checked)
'checked:px-[3px]', 'active:checked:px-[2px]',
// Theme
'theme-preset-3-primary', 'dark:theme-preset-3-primary-dark', 'theme-background-default', 'dark:theme-background-default-dark', 'theme-border-outline border-1', 'dark:theme-border-outline-dark',
// Theme (focus)
'focus-visible:theme-outline-primary focus-visible:outline-2', 'dark:focus-visible:theme-outline-primary-dark',
// Theme (checked)
'checked:theme-preset-1-primary', 'dark:checked:theme-preset-1-primary-dark'];
const SwitchControlHandle = [
// Box
'after:w-[18px] after:md:w-[16px]', 'after:h-[18px] after:md:h-[16px]',
// Shape
'after:rounded-full',
// Theme
'after:active:w-[20px] after:active:md:w-[18px]', 'after:active:h-[20px] after:active:md:h-[18px]', 'after:bg-outline', 'after:dark:bg-outline-dark', 'after:hover:bg-on-surface-variant', 'after:hover:dark:bg-on-surface-variant-dark', 'after:focus-visible:bg-on-surface-variant', 'after:focus-visible:dark:bg-on-surface-variant-dark', 'after:active:bg-outline', 'after:active:dark:bg-outline-dark', 'after:disabled:bg-on-disabled', 'after:disabled:dark:bg-on-disabled-dark', 'after:checked:bg-on-primary', 'after:checked:dark:bg-on-primary-dark', 'after:checked:shadow', 'after:checked:hover:bg-primary-container', 'after:checked:hover:dark:bg-primary-container-dark', 'after:checked:focus-visible:bg-primary-container', 'after:checked:focus-visible:dark:bg-primary-container-dark', 'after:checked:active:bg-on-primary', 'after:checked:active:dark:bg-on-primary-dark', 'after:checked:disabled:bg-on-disabled', 'after:checked:disabled:dark:bg-on-disabled-dark'];
const SwitchLoader = [
// Layout
'layout-row justify-center',
// Box
'w-[24px] md:w-[20px]', 'h-[24px] md:h-[20px]',
// Shape
'rounded-full',
// Theme
'theme-border-outline border-1', 'dark:theme-border-outline-dark'];
const SwitchLoaderHandle = [
// Layout
'after:absolute',
// Box
'after:w-[18px] after:md:w-[14px]', 'after:h-[18px] after:md:h-[14px]',
// Shape
'after:rounded-full',
// Theme
'after:bg-outline', 'dark:after:bg-outline-dark', 'after:disabled:bg-on-disabled', 'dark:after:disabled:bg-on-disabled-dark'];

var script = defineComponent({
  name: 'OrSwitch',
  components: {
    OrLabel: script$1,
    OrLoader: script$2
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: undefined,
      default: undefined
    },
    value: {
      type: undefined,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    labelVariant: {
      type: String,
      default: () => LabelVariant.Toggle
    },
    loading: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change', 'click', 'focus', 'blur'],
  expose: ['root', 'focus', 'blur'],
  setup(props, context) {
    const attributes = reactive({
      id: useIdAttribute(),
      ...useControlAttributes()
    });
    // Refs & Styles
    const root = ref();
    const rootStyles = computed(() => ['or-switch-v3', ...Switch]);
    const control = ref();
    const controlStyles = computed(() => [...SwitchControl, ...SwitchControlHandle]);
    const loaderStyles = computed(() => [...SwitchLoader, ...SwitchLoaderHandle]);
    // State
    const model = useProxyModelValue(toRef(props, 'modelValue'), context.emit);
    const checked = computed(() => {
      if (isArray(model.value)) {
        if (isUndefined(props.value)) {
          throw new Error('[OrSwitch]: `value` must be specified.');
        }
        return model.value.includes(props.value);
      }
      return Boolean(model.value);
    });
    // Handlers
    function onChange() {
      if (isArray(model.value)) {
        if (isUndefined(props.value)) {
          throw new Error('[OrSwitch]: `value` must be specified.');
        }
        if (!checked.value) {
          model.value = include(props.value, model.value);
        } else {
          model.value = exclude(props.value, model.value);
        }
      } else {
        model.value = !model.value;
      }
    }
    // Methods
    function focus() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function blur() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.blur();
    }
    return {
      attributes,
      root,
      rootStyles,
      control,
      controlStyles,
      loaderStyles,
      checked,
      onChange,
      focus,
      blur
    };
  }
});

const _hoisted_1 = ["checked", "required", "disabled"];
const _hoisted_2 = ["disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrLoader = resolveComponent("OrLoader");
  const _component_OrLabel = resolveComponent("OrLabel");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [!_ctx.loading ? (openBlock(), createElementBlock("input", mergeProps({
    key: 0,
    ref: 'control'
  }, _ctx.attributes, {
    class: _ctx.controlStyles,
    type: 'checkbox',
    checked: _ctx.checked,
    required: _ctx.required,
    disabled: _ctx.disabled,
    onChange: _cache[0] || (_cache[0] = $event => {
      _ctx.$emit('change', $event);
      _ctx.onChange();
    }),
    onClick: _cache[1] || (_cache[1] = $event => _ctx.$emit('click', $event)),
    onFocus: _cache[2] || (_cache[2] = $event => _ctx.$emit('focus', $event)),
    onBlur: _cache[3] || (_cache[3] = $event => _ctx.$emit('blur', $event))
  }), null, 16 /* FULL_PROPS */, _hoisted_1)) : (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(_ctx.loaderStyles),
    disabled: _ctx.disabled
  }, [createVNode(_component_OrLoader, {
    variant: 'circular',
    color: 'primary',
    size: 'm'
  })], 10 /* CLASS, PROPS */, _hoisted_2)), _ctx.$slots.default || _ctx.label ? (openBlock(), createBlock(_component_OrLabel, {
    key: 2,
    "control-id": _ctx.attributes.id,
    variant: _ctx.labelVariant,
    selected: _ctx.checked,
    required: _ctx.required,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "default", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["control-id", "variant", "selected", "required", "disabled"])) : createCommentVNode("v-if", true)], 2 /* CLASS */);
}

var css_248z = "\ninput[data-v-4296509a] {\n  line-height: 0;\n  appearance: none;\n}\n";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-4296509a";
script.__file = "src/components/or-switch-v3/OrSwitch.vue";

export { script as s };
